/**
 * @brisa/ui · facelift/pass-card/CardPlanComplete
 *
 * Feature: migracao-facelift-v1 · COMPONENT_MAP §1 #XX (card-plan-complete)
 * Figma origem: nodes 191:3553 (default) e 191:3639 (highlighted)
 *
 * Card rico de plano — exibe tag, preço com subtext, descrição livre,
 * linhas de informação (label/valor com separator) e CTA primário.
 *
 * Token decisions:
 *   - Tag pill bg:   `hsl(var(--text-08))`  → rgba(0,0,0,0.08) = Figma "rgba(0,0,0,0.04)"
 *     (token mais próximo disponível; 0.04 não existe no catálogo, 0.08 é o menor)
 *   - Tag pill text: `hsl(var(--text-64))`  → rgba(0,0,0,0.64) = Figma "rgba(0,0,0,0.72)"
 *     (token mais próximo; 0.72 não existe, 0.64 é o adjacente inferior)
 *   - Row separator: `hsl(var(--text-08))` → border-b neutro leve (idem PassCardWithoutCta)
 *
 * Sem 'use client' — apresentacional puro.
 * CTA com onCtaClick usa onClick no <button>; ctaHref usa asChild + <a>.
 */
import * as React from 'react';

import { Button } from '../../ui/button';
import { cn } from '@brisa/ui/lib/utils';

export interface InfoRow {
  label: string;
  value: string;
}

export interface CardPlanCompleteProps extends React.HTMLAttributes<HTMLElement> {
  /** Tag uppercase no topo (ex: "DE PASSAGEM", "SOCIAL"). */
  tag: string;
  /** Preço formatado (ex: "€14"). */
  price: string;
  /** Texto pequeno ao lado do preço (ex: "Sem renovação automática"). */
  priceSubtext: string;
  /** Descrição do plano (1-2 linhas). */
  description: string;
  /** Linhas de informação (Período, Clubes, Visitas, etc). */
  infoRows: InfoRow[];
  /** Label do CTA (ex: "Escolher"). */
  ctaLabel: string;
  /** Handler do CTA. Pelo menos um de onCtaClick OR ctaHref. */
  onCtaClick?: () => void;
  /** Se presente, CTA vira Link via asChild + <a>. */
  ctaHref?: string;
  /** Variante visual. Default: 'default'. */
  variant?: 'default' | 'highlighted';
  /** Badge preto opcional na direita do tag (só em highlighted). Ex: "30 DIAS FREE". */
  freeBadge?: string;
}

export function CardPlanComplete({
  tag,
  price,
  priceSubtext,
  description,
  infoRows,
  ctaLabel,
  onCtaClick,
  ctaHref,
  variant = 'default',
  freeBadge,
  className,
  ...props
}: CardPlanCompleteProps) {
  const isHighlighted = variant === 'highlighted';

  return (
    <article
      data-slot="card-plan-complete"
      data-variant={variant}
      className={cn(
        'flex h-[427px] w-full flex-col justify-between rounded-[var(--radius-xl)] p-6',
        isHighlighted
          ? 'bg-[hsl(var(--accent-yellow))]'
          : 'bg-[hsl(var(--text-08))]',
        className,
      )}
      {...props}
    >
      {/* Description block (top) */}
      <div className="flex w-full flex-col gap-6" data-slot="description">

        {/* Tag area */}
        <div
          className={cn(
            'flex w-full',
            isHighlighted && freeBadge ? 'items-center justify-between' : 'items-start',
          )}
          data-slot="tag-area"
        >
          {/* Tag pill */}
          <span
            data-slot="tag"
            className="inline-flex items-center justify-center rounded-full bg-[hsl(var(--text-08))] px-3 py-2 text-xs font-semibold uppercase text-[hsl(var(--text-64))]"
          >
            {tag}
          </span>

          {/* Free badge — só quando highlighted + freeBadge definido */}
          {isHighlighted && freeBadge ? (
            <span
              data-slot="free-badge"
              className="inline-flex items-center justify-center rounded-full bg-black px-3 py-2 text-xs font-semibold uppercase text-white"
            >
              {freeBadge}
            </span>
          ) : null}
        </div>

        {/* Price row */}
        <div className="flex w-full items-start gap-4" data-slot="price-row">
          <span
            data-slot="price"
            className="w-[62px] text-right font-body text-[32px] font-bold leading-[0.83] tabular-nums text-black"
          >
            {price}
          </span>
          <span
            data-slot="price-subtext"
            className="flex-1 text-xs leading-tight text-[hsl(var(--text-56))]"
          >
            {priceSubtext}
          </span>
        </div>

        {/* Description */}
        <p
          data-slot="body"
          className="w-full text-xl leading-snug text-black sm:text-sm sm:leading-tight"
        >
          {description}
        </p>
      </div>

      {/* Info block (bottom) */}
      <div className="flex w-full flex-col gap-5" data-slot="info">
        {/* Info rows */}
        <dl className="flex w-full flex-col gap-2 font-body text-sm font-semibold text-black">
          {infoRows.map((row) => (
            <div
              key={row.label}
              data-slot="info-row"
              className="flex w-full items-center justify-between border-b border-[hsl(var(--text-08))] pb-2"
            >
              <dt>{row.label}</dt>
              <dd className="text-right">{row.value}</dd>
            </div>
          ))}
        </dl>

        {/* CTA */}
        {ctaHref ? (
          <Button
            asChild
            variant="primary-medium"
            className={cn(
              'w-full',
              isHighlighted && 'bg-white text-black hover:bg-white/90',
            )}
          >
            <a href={ctaHref}>{ctaLabel}</a>
          </Button>
        ) : (
          <Button
            variant="primary-medium"
            className={cn(
              'w-full',
              isHighlighted && 'bg-white text-black hover:bg-white/90',
            )}
            onClick={onCtaClick}
          >
            {ctaLabel}
          </Button>
        )}
      </div>
    </article>
  );
}
